import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Table,
  Input,
  Breadcrumb,
  Tag,
  DatePicker,
  Row,
  Col,
  Grid,
  message,
  Select,
  Card,
  Statistic,
  Typography,
  Space,
  Dropdown,
  Tooltip,
  Empty,
  Avatar,
  Badge
} from 'antd';
import { Link } from 'react-router-dom';
import {
  RightOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  SearchOutlined,
  StarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SwapOutlined,
  ReloadOutlined,
  CalendarOutlined,
  FilterOutlined,
  MoreOutlined,
  PhoneOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import { walletService } from '../../common/services';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import relativeTime from 'dayjs/plugin/relativeTime';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

dayjs.locale('fr');
dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Hook debounce
const useDebounce = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

// Configuration des types avec couleurs et icônes
const TYPE_CONFIG = {
  points_rechargement: { color: 'green', label: 'Rechargement', icon: '💰' },
  sos_credit_montant: { color: 'orange', label: 'SOS Crédit', icon: '🆘' },
  sos_credit_frais: { color: 'volcano', label: 'Frais SOS', icon: '💸' },
  bonus_dimanche: { color: 'gold', label: 'Bonus Dimanche', icon: '🎁' },
  transfert_uv: { color: 'purple', label: 'Transfert UV', icon: '🔄' },
  maj_classe_service: { color: 'cyan', label: 'Classe Service', icon: '⭐' },
  lier_vehicule: { color: 'blue', label: 'Liaison Véhicule', icon: '🚗' },
  detacher_vehicule: { color: 'magenta', label: 'Détacher Véhicule', icon: '🔓' },
  retrait_points_virement: { color: 'red', label: 'Virement', icon: '💳' },
  bonus_heures_mensuelles: { color: 'lime', label: 'Bonus mensuel', icon: '⏰' },
  bonus_55_courses: { color: 'geekblue', label: 'Bonus 55 courses', icon: '🏆' },
  creation_compte: { color: 'green', label: 'Création compte', icon: '👤' },
  maj_profil: { color: 'blue', label: 'Mise à jour profil', icon: '✏️' },
  bonus_premiere_recharge: { color: 'gold', label: 'Bonus 1re recharge', icon: '🎉' },
  ajout_vehicule_existant: { color: 'geekblue', label: 'Ajout véhicule existant', icon: '🚙' },
  maj_tel: { color: 'orange', label: 'Changement téléphone', icon: '📱' },
};

const HistoriquePoints = () => {
  const [data, setData] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [stats, setStats] = useState({
    totalTransactions: 0,
    totalCredits: 0,
    totalDebits: 0,
    netPoints: 0
  });
  const [todayStats, setTodayStats] = useState({ today: {}, grandTotal: 0 });
  const [statsPeriod, setStatsPeriod] = useState('today'); // 'today' | 'yesterday' | 'week' | 'month' | 'custom'
  const [statsCustomRange, setStatsCustomRange] = useState(null);

  // Filtres
  const [phDateRange, setPhDateRange] = useState(null);
  const [phType, setPhType] = useState(null);
  const [phQuery, setPhQuery] = useState('');
  const phQueryDebounced = useDebounce(phQuery, 400);

  // Pagination/tri
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [sorter, setSorter] = useState({ field: 'date', order: 'descend' });

  // Breakpoints
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const isTablet = screens.sm && !screens.lg;

  // Fetch all pages pour export
  const fetchAllPages = async (baseParams) => {
    const rows = [];
    let pageIndex = 1;
    const pageSize = 1000;
    let totalCount = Infinity;

    while (rows.length < totalCount) {
      const res = await walletService.historiquePoints({
        ...baseParams,
        page: pageIndex,
        limit: pageSize,
      });
      const items = Array.isArray(res?.items) ? res.items : [];
      rows.push(...items);
      totalCount = res?.total ?? rows.length;
      if (!items.length) break;
      pageIndex += 1;
      if (res?.totalPages && pageIndex > res.totalPages) break;
    }
    return rows;
  };

  const mapForExport = (rows) =>
    rows.map((it) => ({
      Type: TYPE_CONFIG[it.type]?.label || it.type,
      Téléphone: formatPhoneNumber(it.phone),
      Description: it.description,
      Points: it.points,
      Date: dayjs(it.date).format('DD/MM/YYYY HH:mm:ss'),
    }));

  // Params API
  const apiParams = useMemo(() => {
    const params = {
      page,
      limit,
      sort: sorter?.field
        ? (sorter.order === 'descend' ? `-${sorter.field}` : `${sorter.field}`)
        : '-date',
    };
    if (phType) params.type = phType;
    // Supprimer les espaces de la recherche pour les numéros de téléphone
    if (phQueryDebounced) params.q = phQueryDebounced.replace(/\s+/g, '');

    if (phDateRange && phDateRange.length === 2 && phDateRange[0] && phDateRange[1]) {
      params.start = phDateRange[0].format('YYYY-MM-DD');
      params.end = phDateRange[1].format('YYYY-MM-DD');
    }
    return params;
  }, [page, limit, sorter, phType, phQueryDebounced, phDateRange]);

  // Chargement
  const load = async () => {
    try {
      setLoading(true);
      const res = await walletService.historiquePoints(apiParams);
      const items = Array.isArray(res?.items) ? res.items : [];
      setData(items);
      setTotal(res?.total || 0);

      // Calculer les stats depuis les données
      const credits = items.filter(i => i.points > 0).reduce((sum, i) => sum + i.points, 0);
      const debits = items.filter(i => i.points < 0).reduce((sum, i) => sum + Math.abs(i.points), 0);
      setStats({
        totalTransactions: res?.total || items.length,
        totalCredits: res?.sumPositive || credits,
        totalDebits: res?.sumNegative || debits,
        netPoints: res?.sumPoints || (credits - debits)
      });

      if (res?.page && res.page !== page) setPage(res.page);
      if (res?.limit && res.limit !== limit) setLimit(res.limit);
    } catch (err) {
      console.error('Erreur API:', err);
      message.error("Impossible de charger l'historique des points.");
    } finally {
      setLoading(false);
    }
  };

  // Calculer les dates selon la période sélectionnée
  const getStatsPeriodParams = () => {
    const today = dayjs();
    switch (statsPeriod) {
      case 'yesterday': {
        const y = today.subtract(1, 'day').format('YYYY-MM-DD');
        return { start: y, end: y };
      }
      case 'week':
        return { start: today.startOf('week').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') };
      case 'month':
        return { start: today.startOf('month').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') };
      case 'custom':
        if (statsCustomRange && statsCustomRange.length === 2 && statsCustomRange[0] && statsCustomRange[1]) {
          return { start: statsCustomRange[0].format('YYYY-MM-DD'), end: statsCustomRange[1].format('YYYY-MM-DD') };
        }
        return {};
      case 'today':
      default:
        return {};
    }
  };

  // Charger les stats par période
  const loadTodayStats = async () => {
    try {
      const params = getStatsPeriodParams();
      const res = await walletService.historiquePointsStatsToday(params);
      if (res) setTodayStats(res);
    } catch (_) {}
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [apiParams]);
  useEffect(() => { loadTodayStats(); /* eslint-disable-next-line */ }, [statsPeriod, statsCustomRange]);

  // Format téléphone
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    let digits = String(phone).replace(/\D/g, '');
    if (digits.startsWith('225')) digits = digits.slice(3);
    if (digits.length === 9) digits = '0' + digits;
    return digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
  };

  // Format nombre
  const formatNumber = (num) => new Intl.NumberFormat('fr-FR').format(num || 0);

  // Exports
  const exportToExcel = async () => {
    try {
      setIsExporting(true);
      message.loading({ content: 'Préparation de l\'export Excel…', key: 'exp' });
      const all = await fetchAllPages(apiParams);
      const rows = mapForExport(all);
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Historique Points');
      XLSX.writeFile(wb, `historique_points_${dayjs().format('YYYY-MM-DD')}.xlsx`);
      message.success({ content: 'Export Excel terminé !', key: 'exp' });
    } catch (e) {
      console.error(e);
      message.error({ content: 'Échec de l\'export Excel.', key: 'exp' });
    } finally {
      setIsExporting(false);
    }
  };

  const exportToCSV = async () => {
    try {
      setIsExporting(true);
      message.loading({ content: 'Préparation de l\'export CSV…', key: 'exp' });
      const all = await fetchAllPages(apiParams);
      const rows = mapForExport(all);
      const ws = XLSX.utils.json_to_sheet(rows);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, `historique_points_${dayjs().format('YYYY-MM-DD')}.csv`);
      message.success({ content: 'Export CSV terminé !', key: 'exp' });
    } catch (e) {
      console.error(e);
      message.error({ content: 'Échec de l\'export CSV.', key: 'exp' });
    } finally {
      setIsExporting(false);
    }
  };

  const exportToPDF = async () => {
    try {
      setIsExporting(true);
      message.loading({ content: 'Préparation du PDF…', key: 'exp' });
      const all = await fetchAllPages(apiParams);
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text('Historique des Points', 14, 22);
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Généré le ${dayjs().format('DD/MM/YYYY à HH:mm')}`, 14, 30);

      autoTable(doc, {
        startY: 38,
        head: [['Type', 'Téléphone', 'Description', 'Points', 'Date']],
        body: all.map((item) => [
          TYPE_CONFIG[item.type]?.label || item.type,
          formatPhoneNumber(item.phone),
          item.description?.substring(0, 50) + (item.description?.length > 50 ? '...' : ''),
          String(item.points),
          dayjs(item.date).format('DD/MM/YYYY HH:mm'),
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [114, 46, 209] },
        columnStyles: {
          2: { cellWidth: 60 },
        },
      });
      doc.save(`historique_points_${dayjs().format('YYYY-MM-DD')}.pdf`);
      message.success({ content: 'Export PDF terminé !', key: 'exp' });
    } catch (e) {
      console.error(e);
      message.error({ content: 'Échec de l\'export PDF.', key: 'exp' });
    } finally {
      setIsExporting(false);
    }
  };

  const exportMenuItems = [
    {
      key: 'excel',
      label: 'Excel (.xlsx)',
      icon: <FileExcelOutlined style={{ color: '#217346' }} />,
      onClick: exportToExcel,
    },
    {
      key: 'csv',
      label: 'CSV',
      icon: <DownloadOutlined style={{ color: '#1890ff' }} />,
      onClick: exportToCSV,
    },
    {
      key: 'pdf',
      label: 'PDF',
      icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />,
      onClick: exportToPDF,
    },
  ];

  // Options du filtre type
  const typeOptions = Object.entries(TYPE_CONFIG).map(([value, config]) => ({
    value,
    label: (
      <span>
        <span style={{ marginRight: 8 }}>{config.icon}</span>
        {config.label}
      </span>
    ),
  }));

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: isMobile ? 120 : 160,
      render: (type) => {
        const config = TYPE_CONFIG[type] || { color: 'default', label: type, icon: '📋' };
        return (
          <Tag
            color={config.color}
            style={{
              borderRadius: 6,
              padding: '2px 10px',
              fontSize: isMobile ? 11 : 12,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <span>{config.icon}</span>
            {!isMobile && config.label}
          </Tag>
        );
      },
      sorter: true,
    },
    {
      title: 'Téléphone',
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
      render: (phone) => (
        <Text copyable={{ text: phone }} style={{ fontFamily: 'monospace', fontSize: 13 }}>
          <PhoneOutlined style={{ marginRight: 6, color: '#8c8c8c' }} />
          {formatPhoneNumber(phone)}
        </Text>
      ),
      responsive: ['sm'],
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text, record) => (
        <Tooltip title={text}>
          <div style={{ maxWidth: 300 }}>
            <Text
              ellipsis
              style={{ fontSize: 13 }}
            >
              {text}
            </Text>
            {isMobile && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  <PhoneOutlined style={{ marginRight: 4 }} />
                  {formatPhoneNumber(record.phone)}
                </Text>
              </div>
            )}
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Points',
      dataIndex: 'points',
      key: 'points',
      width: 100,
      align: 'right',
      render: (points) => {
        const isPositive = points >= 0;
        return (
          <Tag
            color={isPositive ? 'success' : 'error'}
            style={{
              fontWeight: 600,
              fontSize: isMobile ? 12 : 13,
              padding: '4px 12px',
              borderRadius: 20,
              minWidth: 70,
              textAlign: 'center',
            }}
          >
            {isPositive ? '+' : ''}{formatNumber(points)}
          </Tag>
        );
      },
      sorter: true,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: isMobile ? 100 : 160,
      render: (date) => (
        <Tooltip title={dayjs(date).format('dddd DD MMMM YYYY à HH:mm:ss')}>
          <Space size={4} direction={isMobile ? 'vertical' : 'horizontal'}>
            <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
            <span style={{ fontSize: isMobile ? 11 : 13 }}>
              {isMobile ? dayjs(date).format('DD/MM HH:mm') : dayjs(date).format('DD/MM/YYYY HH:mm')}
            </span>
          </Space>
        </Tooltip>
      ),
      sorter: true,
      defaultSortOrder: 'descend',
    },
  ];

  // Stats cards par type pour aujourd'hui
  const todayTypeColors = {
    points_rechargement:     { bg: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)', color: '#52c41a' },
    sos_credit_montant:      { bg: 'linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%)', color: '#fa8c16' },
    sos_credit_frais:        { bg: 'linear-gradient(135deg, #fff2e8 0%, #ffd8bf 100%)', color: '#fa541c' },
    bonus_dimanche:          { bg: 'linear-gradient(135deg, #fffbe6 0%, #fff1b8 100%)', color: '#faad14' },
    transfert_uv:            { bg: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)', color: '#722ed1' },
    maj_classe_service:      { bg: 'linear-gradient(135deg, #e6fffb 0%, #b5f5ec 100%)', color: '#13c2c2' },
    lier_vehicule:           { bg: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)', color: '#1890ff' },
    detacher_vehicule:       { bg: 'linear-gradient(135deg, #fff0f6 0%, #ffd6e7 100%)', color: '#eb2f96' },
    retrait_points_virement: { bg: 'linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%)', color: '#ff4d4f' },
    bonus_heures_mensuelles: { bg: 'linear-gradient(135deg, #fcffe6 0%, #eaff8f 100%)', color: '#a0d911' },
    bonus_55_courses:        { bg: 'linear-gradient(135deg, #f0f5ff 0%, #d6e4ff 100%)', color: '#2f54eb' },
    creation_compte:         { bg: 'linear-gradient(135deg, #f6ffed 0%, #b7eb8f 100%)', color: '#389e0d' },
    maj_profil:              { bg: 'linear-gradient(135deg, #e6f7ff 0%, #91d5ff 100%)', color: '#096dd9' },
    bonus_premiere_recharge: { bg: 'linear-gradient(135deg, #fffbe6 0%, #ffe58f 100%)', color: '#d4b106' },
    ajout_vehicule_existant: { bg: 'linear-gradient(135deg, #f0f5ff 0%, #adc6ff 100%)', color: '#2f54eb' },
    maj_tel:                 { bg: 'linear-gradient(135deg, #fff7e6 0%, #ffd591 100%)', color: '#fa8c16' },
  };

  const todayCards = Object.entries(TYPE_CONFIG).map(([key, config]) => {
    const stat = todayStats.today?.[key];
    const colors = todayTypeColors[key] || { bg: '#fafafa', color: '#595959' };
    return {
      key,
      label: config.label,
      icon: config.icon,
      count: stat?.count || 0,
      totalPoints: stat?.totalPoints || 0,
      bg: colors.bg,
      color: colors.color,
    };
  });

  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb
        separator={<RightOutlined />}
        style={{ marginBottom: 20 }}
        items={[
          { title: <Link to="/dashboard">Accueil</Link> },
          { title: <span>Wallet</span> },
          { title: <span style={{ fontWeight: 600 }}>Historique Points</span> },
        ]}
      />

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 24,
        flexWrap: 'wrap',
        gap: 16,
      }}>
        <div>
          <Title level={isMobile ? 4 : 3} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
            <StarOutlined style={{ color: '#722ed1' }} />
            Historique des Points
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            Suivi de toutes les transactions de points
          </Text>
        </div>
        <Space wrap>
          <Button
            icon={<ReloadOutlined spin={isLoading} />}
            onClick={load}
            disabled={isLoading}
          >
            {!isMobile && 'Actualiser'}
          </Button>
          <Dropdown
            menu={{ items: exportMenuItems }}
            placement="bottomRight"
            disabled={isExporting}
          >
            <Button type="primary" icon={<DownloadOutlined />} loading={isExporting}>
              Exporter {!isMobile && <MoreOutlined />}
            </Button>
          </Dropdown>
        </Space>
      </div>

      {/* Stats par période et type */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <CalendarOutlined style={{ color: '#722ed1' }} />
          <Text strong style={{ fontSize: 14 }}>
            {{ today: "Aujourd'hui", yesterday: 'Hier', week: 'Cette semaine', month: 'Ce mois', custom: 'Période' }[statsPeriod]} — {todayStats.grandTotal || 0} transaction{(todayStats.grandTotal || 0) > 1 ? 's' : ''}
          </Text>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {['today', 'yesterday', 'week', 'month', 'custom'].map((key) => (
              <Button
                key={key}
                size="small"
                type={statsPeriod === key ? 'primary' : 'default'}
                onClick={() => setStatsPeriod(key)}
                style={{
                  borderRadius: 16,
                  fontSize: 12,
                  ...(statsPeriod === key ? {} : { borderColor: '#d9d9d9' }),
                }}
              >
                {{ today: "Aujourd'hui", yesterday: 'Hier', week: 'Semaine', month: 'Mois', custom: 'Personnalisé' }[key]}
              </Button>
            ))}
            {statsPeriod === 'custom' && (
              <RangePicker
                size="small"
                value={statsCustomRange}
                onChange={(dates) => setStatsCustomRange(dates)}
                format="DD/MM/YYYY"
                style={{ borderRadius: 8 }}
                allowClear={false}
              />
            )}
          </div>
        </div>
        <Row gutter={[12, 12]}>
          {todayCards.map((card) => (
            <Col xs={8} sm={6} md={4} lg={3} key={card.key}>
              <Card
                hoverable
                style={{
                  background: card.bg,
                  border: 'none',
                  borderRadius: 12,
                  height: '100%',
                  cursor: 'pointer',
                  opacity: card.count > 0 ? 1 : 0.5,
                }}
                bodyStyle={{ padding: isMobile ? 10 : 14, textAlign: 'center' }}
                onClick={() => { setPhType(card.key); setPage(1); }}
              >
                <div style={{ fontSize: isMobile ? 20 : 24, marginBottom: 4 }}>{card.icon}</div>
                <Text
                  style={{
                    fontSize: isMobile ? 22 : 28,
                    fontWeight: 700,
                    color: card.color,
                    display: 'block',
                    lineHeight: 1.2,
                  }}
                >
                  {card.count}
                </Text>
                <Text
                  style={{
                    fontSize: isMobile ? 10 : 11,
                    color: '#595959',
                    display: 'block',
                    marginTop: 2,
                    lineHeight: 1.2,
                  }}
                >
                  {card.label}
                </Text>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Filtres */}
      <Card
        style={{ marginBottom: 24, borderRadius: 12 }}
        bodyStyle={{ padding: isMobile ? 16 : 20 }}
      >
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8} lg={7}>
            <RangePicker
              size={isMobile ? 'middle' : 'large'}
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              value={phDateRange}
              onChange={(dates) => { setPage(1); setPhDateRange(dates); }}
              placeholder={['Date début', 'Date fin']}
              allowClear
              suffixIcon={<CalendarOutlined />}
            />
          </Col>
          <Col xs={24} md={8} lg={6}>
            <Select
              size={isMobile ? 'middle' : 'large'}
              allowClear
              style={{ width: '100%' }}
              placeholder={
                <span>
                  <FilterOutlined style={{ marginRight: 8 }} />
                  Filtrer par type
                </span>
              }
              value={phType}
              onChange={(val) => { setPage(1); setPhType(val); }}
              options={typeOptions}
              optionFilterProp="label"
              showSearch
            />
          </Col>
          <Col xs={24} md={8} lg={11}>
            <Input
              size={isMobile ? 'middle' : 'large'}
              allowClear
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Rechercher par description, téléphone..."
              value={phQuery}
              onChange={(e) => { setPage(1); setPhQuery(e.target.value); }}
              style={{ borderRadius: 8 }}
            />
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 0 }}>
        <Table
          loading={isLoading}
          size={isMobile ? 'small' : 'middle'}
          columns={columns}
          dataSource={data}
          rowKey={(record) => record._id || `${record.phone}-${record.date}-${Math.random()}`}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: !isMobile,
            showQuickJumper: !isMobile,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => (
              <Text type="secondary" style={{ fontSize: 13 }}>
                {`${range[0]}-${range[1]} sur ${formatNumber(total)} transactions`}
              </Text>
            ),
            style: { padding: '12px 16px', margin: 0 },
          }}
          onChange={(pagination, _filters, sorterArg) => {
            setPage(pagination.current);
            setLimit(pagination.pageSize);
            const s = Array.isArray(sorterArg) ? sorterArg[0] : sorterArg;
            if (s && s.field) {
              setSorter({ field: s.field, order: s.order });
            } else {
              setSorter({ field: 'date', order: 'descend' });
            }
          }}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Text type="secondary">
                    Aucune transaction trouvée
                  </Text>
                }
              />
            ),
          }}
          rowClassName={(_, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
        />
      </Card>

      {/* Styles */}
      <style>{`
        .table-row-light {
          background-color: #ffffff;
        }
        .table-row-dark {
          background-color: #fafafa;
        }
        .table-row-light:hover td,
        .table-row-dark:hover td {
          background-color: #f9f0ff !important;
        }
        .ant-table-thead > tr > th {
          background-color: #fafafa !important;
          font-weight: 600 !important;
          color: #262626 !important;
        }
        .ant-pagination {
          background-color: #fafafa;
          border-top: 1px solid #f0f0f0;
        }
        .ant-select-selection-item span {
          display: flex;
          align-items: center;
        }
      `}</style>
    </div>
  );
};

export default HistoriquePoints;
